/* Created by Interactive 63 - https://interactive63.com */
/**
 * Builds the pivot rulers: measuring lines from an element's pivot to the edges of up to four
 * targets (the layer's own box, the shape under the pivot, the nearest other shapes, and the
 * artboard). Measurements
 * are taken in the layer's own axes at the playhead, so for a rotated layer the lines rotate
 * with it. The numeric work is in measure.ts; this file gathers the boxes from the rendered
 * scene.
 */
import type { ElementId } from '../../../core/model/types';
import type { Store } from '../../store/store';
import { applyMatrix, invertMatrix, pivotLocal, type Matrix, type Point } from './geometry';
import {
  boxInFrame,
  formatDistance,
  measureFromPivot,
  nearestEdges,
  pickTargetUnderPivot,
  type Box,
  type TargetCandidate,
} from './measure';
import type { Ruler, RulerLine } from './overlay';

export interface MeasureTargets {
  layer: boolean;
  shape: boolean;
  /** The first edge of any other shape that each ruler direction runs into. */
  neighbour: boolean;
  artboard: boolean;
}

/** What the rulers need from the stage. */
export interface RulerSource {
  store: Store;
  node(id: ElementId): SVGGraphicsElement | undefined;
  getElement(id: ElementId): Element | undefined;
  screenMatrix(node: SVGGraphicsElement): Matrix | null;
  /** The root `<svg>` element of the rendered scene. */
  rootSvg(): SVGSVGElement | null;
  /** Elements under a client point, topmost first. */
  elementsAt(x: number, y: number): Element[];
}

export interface ShapeCandidate extends TargetCandidate {
  element: Element;
}

const MAX_SHAPES_CHECKED = 400;
const MAX_NEIGHBOURS_CHECKED = 1500;

/** Containers whose children are never drawn directly, so they cannot be in the way. */
const NON_RENDERED =
  'defs, clipPath, mask, pattern, symbol, marker, linearGradient, radialGradient';

/** A box from an SVG bounding box. */
function boxOf(b: { x: number; y: number; width: number; height: number }): Box {
  return { left: b.x, top: b.y, right: b.x + b.width, bottom: b.y + b.height };
}

/** Whether an element is a basic shape or path, which can answer point-in-fill queries. */
export function isGeometry(el: Element): el is SVGGeometryElement {
  return typeof (el as Partial<SVGGeometryElement>).isPointInFill === 'function';
}

/** How many ancestors an element has inside the artboard. */
function depthOf(el: Element): number {
  let depth = 0;
  for (let p = el.parentElement; p && !p.classList.contains('artboard'); p = p.parentElement) {
    depth += 1;
  }
  return depth;
}

/** Whether a client point lies inside a shape's fill or stroke, whatever its paint. */
export function containsPoint(
  source: RulerSource,
  shape: SVGGeometryElement,
  client: Point,
): boolean {
  const matrix = source.screenMatrix(shape);
  const inverse = matrix ? invertMatrix(matrix) : null;
  if (!inverse) return false;
  const local = applyMatrix(inverse, client);
  const point = new DOMPoint(local.x, local.y);
  try {
    return shape.isPointInFill(point) || shape.isPointInStroke(point);
  } catch {
    return false;
  }
}

/** Shapes under a client point, topmost first: those inside the layer, then any others. */
export function shapeCandidates(
  source: RulerSource,
  layerId: ElementId,
  pivot: Point,
): ShapeCandidate[] {
  const layer = source.getElement(layerId);
  if (!layer) return [];
  const inside: ShapeCandidate[] = [];
  const nodes = [layer, ...Array.from(layer.querySelectorAll('[data-st-id]'))];
  for (const el of nodes.slice(0, MAX_SHAPES_CHECKED)) {
    const id = el.getAttribute('data-st-id');
    if (!id || !isGeometry(el)) continue;
    inside.push({
      id,
      element: el,
      depth: depthOf(el),
      contains: containsPoint(source, el, pivot),
      insideLayer: true,
    });
  }
  inside.reverse();
  const outside: ShapeCandidate[] = [];
  for (const el of source.elementsAt(pivot.x, pivot.y)) {
    const owner = el.closest('[data-st-id]');
    const id = owner?.getAttribute('data-st-id');
    if (!owner || !id || layer.contains(owner) || !isGeometry(owner)) continue;
    outside.push({ id, element: owner, depth: depthOf(owner), contains: true, insideLayer: false });
  }
  return [...inside, ...outside];
}

/** A shape's bounding box expressed in a layer's own axes. */
export function shapeBoxInLayer(
  source: RulerSource,
  layerId: ElementId,
  shape: Element,
): Box | null {
  const node = source.node(layerId);
  const nodeMatrix = node ? source.screenMatrix(node) : null;
  const shapeMatrix = source.screenMatrix(shape as SVGGraphicsElement);
  if (!nodeMatrix || !shapeMatrix) return null;
  try {
    return boxInFrame(boxOf((shape as SVGGraphicsElement).getBBox()), shapeMatrix, nodeMatrix);
  } catch {
    return null;
  }
}

/** The boxes, in the layer's own axes, of every visible shape outside the layer. */
function neighbourBoxes(source: RulerSource, layerId: ElementId): Box[] {
  const layer = source.getElement(layerId);
  const root = source.rootSvg();
  if (!layer || !root) return [];
  const boxes: Box[] = [];
  const candidates = Array.from(root.querySelectorAll('[data-st-id]')).slice(
    0,
    MAX_NEIGHBOURS_CHECKED,
  );
  for (const el of candidates) {
    if (!isGeometry(el) || layer.contains(el) || el.contains(layer)) continue;
    if (el.closest(NON_RENDERED) || getComputedStyle(el).display === 'none') continue;
    const box = shapeBoxInLayer(source, layerId, el);
    if (box) boxes.push(box);
  }
  return boxes;
}

/** Builds the ruler to the nearest other shapes; directions with nothing ahead are left out. */
function neighbourRuler(boxes: readonly Box[], pivot: Point, toClient: (p: Point) => Point): Ruler {
  const hits = nearestEdges(pivot, boxes);
  const from = toClient(pivot);
  const ends: Record<RulerLine['direction'], Point> = {
    left: { x: pivot.x - (hits.left ?? 0), y: pivot.y },
    right: { x: pivot.x + (hits.right ?? 0), y: pivot.y },
    up: { x: pivot.x, y: pivot.y - (hits.up ?? 0) },
    down: { x: pivot.x, y: pivot.y + (hits.down ?? 0) },
  };
  const lines: RulerLine[] = [];
  for (const direction of ['left', 'right', 'up', 'down'] as const) {
    const distance = hits[direction];
    if (distance === undefined) continue;
    lines.push({
      direction,
      from,
      to: toClient(ends[direction]),
      label: formatDistance(distance),
    });
  }
  return { target: 'neighbour', lines, centredX: false, centredY: false };
}

/** Builds the lines and flags for one target box. */
function rulerFor(
  target: Ruler['target'],
  box: Box,
  pivot: Point,
  toClient: (p: Point) => Point,
): Ruler {
  const m = measureFromPivot(pivot, box);
  const from = toClient(pivot);
  const lines: RulerLine[] = [
    { direction: 'left', to: { x: pivot.x - m.left, y: pivot.y }, value: m.left },
    { direction: 'right', to: { x: pivot.x + m.right, y: pivot.y }, value: m.right },
    { direction: 'up', to: { x: pivot.x, y: pivot.y - m.up }, value: m.up },
    { direction: 'down', to: { x: pivot.x, y: pivot.y + m.down }, value: m.down },
  ].map((line) => ({
    direction: line.direction as RulerLine['direction'],
    from,
    to: toClient(line.to),
    label: formatDistance(line.value),
  }));
  return { target, lines, centredX: m.centredX, centredY: m.centredY };
}

export interface RulerResult {
  rulers: Ruler[];
  /** The label of the shape being measured, for the Measure popover. */
  shapeId: ElementId | undefined;
}

/** Computes the rulers for an element's pivot at the current playhead. */
export function computeRulers(
  source: RulerSource,
  id: ElementId,
  targets: MeasureTargets,
): RulerResult {
  const node = source.node(id);
  const meta = source.store.getState().project.elements[id];
  const matrix = node ? source.screenMatrix(node) : null;
  if (!node || !meta || !matrix) return { rulers: [], shapeId: undefined };
  let nodeBox: DOMRect;
  try {
    nodeBox = node.getBBox();
  } catch {
    return { rulers: [], shapeId: undefined };
  }
  const pivot = pivotLocal(nodeBox, meta.origin);
  const toClient = (p: Point): Point => applyMatrix(matrix, p);
  const rulers: Ruler[] = [];
  let shapeId: ElementId | undefined;

  if (targets.layer) rulers.push(rulerFor('layer', boxOf(nodeBox), pivot, toClient));

  // A group measures to its own box and to other layers, never to the shapes inside it.
  const isGroup = meta.childIds.length > 0;
  if (targets.shape && !isGroup) {
    const candidates = shapeCandidates(source, id, toClient(pivot));
    const pickedId = pickTargetUnderPivot(candidates);
    const picked = candidates.find((c) => c.id === pickedId);
    const box = picked ? shapeBoxInLayer(source, id, picked.element) : null;
    if (picked && box) {
      shapeId = picked.id;
      rulers.push(rulerFor('shape', box, pivot, toClient));
    }
  }

  if (targets.neighbour) {
    const ruler = neighbourRuler(neighbourBoxes(source, id), pivot, toClient);
    if (ruler.lines.length > 0) rulers.push(ruler);
  }

  if (targets.artboard) {
    const root = source.rootSvg();
    const rootMatrix = root ? source.screenMatrix(root) : null;
    const { viewBox } = source.store.getState().project.svg;
    if (rootMatrix) {
      const box = boxInFrame(boxOf(viewBox), rootMatrix, matrix);
      rulers.push(rulerFor('artboard', box, pivot, toClient));
    }
  }
  return { rulers, shapeId };
}
